import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Typography,
} from '@mui/material';
import AutoAwesomeMosaicOutlinedIcon from '@mui/icons-material/AutoAwesomeMosaicOutlined';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import { Day, Stop, Trip } from '../../types';
import { outputSurface, serifVoice } from '../../theme';
import {
  Board,
  MODE_LABELS,
  StopPatch,
  TripBasics,
  applyPlan,
  createStops,
  fetchBoard,
  formatDay,
  keepAllPatches,
  openChoices,
  patchStop,
  patchTrip,
  placePatch,
  planDays,
  removeStop,
  shortTime,
  updateDay,
  updateTripBasics,
} from '../../lib/plan';
import { formatHours } from '../../lib/booking';
import { bySortKey } from '../../lib/sortKey';
import TripTree from './TripTree';
import CandidatesPanel from './CandidatesPanel';
import ResearchDialog from './ResearchDialog';
import InlineEdit from './InlineEdit';
import TripBasicsForm, { basicsProblem, cleanBasics } from './TripBasicsForm';
import { EndDialog, EndKind, LegDialog, NightsDialog } from './BoardDialogs';
import { MoveTarget } from './PlaceCard';
import { useElapsed } from './useElapsed';

/**
 * The planning board. The trip is the anchor on the left — a vertical tree of
 * town → day → places — and the candidates sit on the right, to be dragged onto
 * days. "Plan the days" fits the candidates into the days there are, as
 * suggestions the traveller keeps or drops in place.
 */
export default function PlanBoardPage() {
  const { tripId } = useParams<{ tripId: string }>();
  const [board, setBoard] = useState<Board | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [planning, setPlanning] = useState(false);
  const planElapsed = useElapsed(planning);
  const [dialog, setDialog] = useState<'basics' | 'research' | null>(null);
  const [researchFocus, setResearchFocus] = useState('');
  const [leg, setLeg] = useState<Stop | null>(null);
  const [end, setEnd] = useState<EndKind | null>(null);
  const [nights, setNights] = useState<Day[] | null>(null);

  const reload = useCallback(async () => {
    if (!tripId) return;
    const { data, error } = await fetchBoard(tripId);
    setLoadError(error);
    setBoard(data);
    setLoading(false);
  }, [tripId]);

  useEffect(() => {
    reload();
  }, [reload]);

  /** Optimistic write: change local state now, roll back by reloading on failure. */
  const commit = useCallback(
    async (local: (b: Board) => Board, write: () => Promise<{ error: string | null }>) => {
      setBoard((b) => (b ? local(b) : b));
      const { error } = await write();
      if (error) {
        setToast(`Couldn’t save: ${error}`);
        reload();
      }
    },
    [reload],
  );

  const patchLocal = (stopId: string, patch: StopPatch) => (b: Board) => ({
    ...b,
    stops: b.stops.map((s) => (s.stop_id === stopId ? { ...s, ...patch } : s)).sort(bySortKey),
  });

  const place = useCallback(
    (stopId: string, dayId: string | null, beforeId: string | null) => {
      if (!board) return;
      // A candidate dropped back on the panel (often an accidental drag) is
      // already where it belongs; don't wipe its suggestion or reason.
      if (dayId === null && !board.stops.find((s) => s.stop_id === stopId)?.day_id) return;
      const patch = placePatch(board, stopId, dayId, beforeId);
      commit(patchLocal(stopId, patch), () => patchStop(stopId, patch));
    },
    [board, commit],
  );

  const keep = useCallback((s: Stop) => s.suggested_day_id && place(s.stop_id, s.suggested_day_id, null), [place]);

  const reject = useCallback(
    (s: Stop) => {
      const patch = { suggested_day_id: null, plan_note: null };
      commit(patchLocal(s.stop_id, patch), () => patchStop(s.stop_id, patch));
    },
    [commit],
  );

  const toggleMustSee = useCallback(
    (s: Stop) => {
      const patch = { must_see: !s.must_see };
      commit(patchLocal(s.stop_id, patch), () => patchStop(s.stop_id, patch));
    },
    [commit],
  );

  const remove = useCallback(
    (s: Stop) =>
      commit(
        (b) => ({ ...b, stops: b.stops.filter((x) => x.stop_id !== s.stop_id) }),
        () => removeStop(s.stop_id),
      ),
    [commit],
  );

  const addCandidate = useCallback(
    async (title: string) => {
      if (!board) return;
      const { data, error } = await createStops(board.trip.trip_id, [{ title }]);
      if (error) setToast(`Couldn’t add: ${error}`);
      else setBoard((b) => (b ? { ...b, stops: [...b.stops, ...data] } : b));
    },
    [board],
  );

  async function runPlan() {
    if (!board) return;
    setPlanning(true);
    const { data, error } = await planDays(board.trip.trip_id);
    if (error || !data) {
      setPlanning(false);
      setToast(error ?? 'Nothing came back. Try again.');
      return;
    }
    // Apply against the board as it is now, not as it was a minute ago.
    const fresh = (await fetchBoard(board.trip.trip_id)).data ?? board;
    const applied = await applyPlan(fresh, data);
    setPlanning(false);
    if (applied.error) setToast(`Couldn’t save the plan: ${applied.error}`);
    await reload();
  }

  async function keepAll() {
    if (!board) return;
    const patches = keepAllPatches(board);
    setBoard((b) => (b ? patches.reduce((acc, p) => patchLocal(p.stopId, p.patch)(acc), b) : b));
    const results = await Promise.all(patches.map((p) => patchStop(p.stopId, p.patch)));
    const failed = results.find((r) => r.error);
    if (failed?.error) {
      setToast(`Couldn’t keep them all: ${failed.error}`);
      reload();
    } else {
      setToast(`${patches.length} ${patches.length === 1 ? 'place' : 'places'} kept on their days.`);
    }
  }

  async function clearSuggestions() {
    if (!board) return;
    const ids = board.stops.filter((s) => !s.day_id && (s.suggested_day_id || s.plan_note)).map((s) => s.stop_id);
    setBoard((b) =>
      b
        ? {
            ...b,
            trip: { ...b.trip, plan_summary: null },
            stops: b.stops.map((s) => (ids.includes(s.stop_id) ? { ...s, suggested_day_id: null, plan_note: null } : s)),
          }
        : b,
    );
    const results = await Promise.all([
      ...ids.map((id) => patchStop(id, { suggested_day_id: null, plan_note: null })),
      patchTrip(board.trip.trip_id, { plan_summary: null }),
    ]);
    if (results.some((r) => r.error)) reload();
  }

  const view = useMemo(() => {
    if (!board) return null;
    const targets: MoveTarget[] = [
      { dayId: null, label: 'Candidates' },
      ...board.days.map((d, i) => ({ dayId: d.day_id, label: `Day ${i + 1} · ${formatDay(d.date)}${d.base_city ? ` · ${d.base_city}` : ''}` })),
    ];
    const candidates = board.stops.filter((s) => !s.day_id && s.stop_type !== 'transit');
    const suggestions = candidates.filter((s) => s.suggested_day_id);
    return { targets, candidates, suggestions, choices: openChoices(board) };
  }, [board]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (loadError) return <Alert severity="error">{loadError}</Alert>;
  if (!board || !view) return <Typography color="text.secondary">Trip not found.</Typography>;

  const { trip, days } = board;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
      {/* ── 1 + 2: start, finish, destination, themes ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0 }}>
          <Box component="h1" sx={{ m: 0 }}>
            <InlineEdit
              value={trip.title}
              label="Trip name"
              textSx={{ fontSize: { xs: '1.75rem', md: '2.125rem' }, fontWeight: 600, lineHeight: 1.235, color: 'primary.main' }}
              onSave={(title) => {
                // A trip always has a name; clearing it keeps the old one.
                if (title) commit((b) => ({ ...b, trip: { ...b.trip, title } }), () => patchTrip(trip.trip_id, { title }));
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            {trip.destination ? <Typography sx={{ fontWeight: 500 }}>{trip.destination}</Typography> : null}
            {trip.themes.map((t) => (
              <Chip key={t} label={t} size="small" variant="outlined" />
            ))}
            {trip.dates_precision !== 'exact' ? <Chip label="dates may move" size="small" color="warning" variant="outlined" /> : null}
          </Box>
          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 1 }}>
            <EndLine kind="start" trip={trip} onOpen={setEnd} />
            <EndLine kind="end" trip={trip} onOpen={setEnd} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => setDialog('basics')}>
            Trip basics
          </Button>
          <Button size="small" component={RouterLink} to={`/trips/${trip.trip_id}/edit`}>
            Publishing
          </Button>
        </Box>
      </Box>

      {/* ── Roamoir's help ── */}
      <Box
        sx={{
          bgcolor: outputSurface.bg,
          color: outputSurface.text,
          borderRadius: 2,
          p: { xs: 2, sm: 2.25 },
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
          {planning ? (
            <>
              <Typography sx={{ fontWeight: 600, color: '#FFFFFF' }}>Planning the days… {planElapsed}s</Typography>
              <Typography variant="body2" sx={{ color: outputSurface.muted }}>
                Fitting {view.candidates.length} candidates into {days.length} days. Usually under a minute.
              </Typography>
            </>
          ) : trip.plan_summary && view.suggestions.length ? (
            <>
              <Typography sx={{ fontWeight: 600, color: '#FFFFFF' }}>
                {view.suggestions.length} {view.suggestions.length === 1 ? 'suggestion' : 'suggestions'} on the days. Keep or drop each one.
              </Typography>
              <Typography variant="body2" sx={{ ...serifVoice, mt: 0.5, lineHeight: 1.55 }}>
                {trip.plan_summary}
              </Typography>
            </>
          ) : (
            <>
              <Typography sx={{ fontWeight: 600, color: '#FFFFFF' }}>{headline(board, view.candidates.length)}</Typography>
              <Typography variant="body2" sx={{ color: outputSurface.muted }}>
                {view.candidates.length
                  ? `${view.candidates.length} candidates, ${days.length} days. Roamoir can fit them in for you, or drag them yourself.`
                  : 'Research places, paste a list from ChatGPT, or add your own.'}
              </Typography>
            </>
          )}
        </Box>
        {trip.plan_summary && view.suggestions.length && !planning ? (
          <>
            <Button variant="contained" color="secondary" onClick={keepAll}>
              Keep all {view.suggestions.length}
            </Button>
            <DarkButton onClick={clearSuggestions}>Clear suggestions</DarkButton>
            <DarkButton onClick={runPlan}>Plan again</DarkButton>
          </>
        ) : (
          <>
            <Button
              variant="contained"
              color="secondary"
              startIcon={planning ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeMosaicOutlinedIcon />}
              onClick={runPlan}
              disabled={planning || view.candidates.length === 0 || days.length === 0}
            >
              Plan the days
            </Button>
            <DarkButton startIcon={<TravelExploreIcon />} onClick={() => {
              setResearchFocus('');
              setDialog('research');
            }} disabled={!trip.destination || planning}>
              Research places
            </DarkButton>
            <DarkButton startIcon={<ContentPasteIcon />} component={RouterLink} to={`/trips/${trip.trip_id}/paste`}>
              Paste a list
            </DarkButton>
          </>
        )}
      </Box>

      {/* ── Decide later ── */}
      {view.choices.length ? (
        <Box
          component="section"
          aria-labelledby="decide-later"
          sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, px: 1.75, py: 1.25, borderRadius: 2, border: '1px solid #EBC9B9', bgcolor: '#FBF1EC' }}
        >
          <Typography id="decide-later" component="h2" sx={{ fontSize: '0.9rem', fontWeight: 700, color: 'secondary.dark', mr: 0.5 }}>
            Decide later
          </Typography>
          {view.choices.map((c) => (
            <Chip
              key={c.key}
              label={`${c.label}${c.by ? ` · by ${formatDay(c.by)}` : ''}`}
              onClick={() => {
                if (c.target.kind === 'leg') {
                  const stopId = c.target.stopId;
                  setLeg(board.stops.find((s) => s.stop_id === stopId) ?? null);
                } else setEnd(c.target.kind);
              }}
              sx={{ bgcolor: '#FFFFFF', border: '1px solid #E3B8A4' }}
            />
          ))}
        </Box>
      ) : null}

      {/* ── The board: the trip anchors the left, candidates on the right ── */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.5, alignItems: 'flex-start' }}>
        <Box sx={{ flex: '999 1 600px', minWidth: 0 }}>
          <TripTree
            board={board}
            targets={view.targets}
            onPlace={place}
            onKeep={keep}
            onReject={reject}
            onToggleMustSee={toggleMustSee}
            onRemove={remove}
            onOpenLeg={setLeg}
            onEditNights={setNights}
            onLabelDay={(day, label) =>
              commit(
                (b) => ({ ...b, days: b.days.map((d) => (d.day_id === day.day_id ? { ...d, label } : d)) }),
                () => updateDay(day.day_id, { label }),
              )
            }
            onResearchLayover={(l) => {
              setResearchFocus(
                `A ${l.hours !== null ? `${formatHours(l.hours)} ` : ''}layover in ${l.place}${l.from && l.until ? `, ${l.from} to ${l.until}` : ''}: food and lounges in or near the airport or station, and whether going into town is realistic in the time after connections. Set the town to ${l.place}.`,
              );
              setDialog('research');
            }}
          />
        </Box>
        <Box sx={{ flex: '1 1 340px', maxWidth: { md: 400 }, minWidth: 0, alignSelf: 'stretch' }}>
          <CandidatesPanel
            board={board}
            targets={view.targets}
            onPlace={place}
            onToggleMustSee={toggleMustSee}
            onRemove={remove}
            onAdd={addCandidate}
            onResearch={() => {
              setResearchFocus('');
              setDialog('research');
            }}
          />
        </Box>
      </Box>

      <BasicsDialog
        open={dialog === 'basics'}
        board={board}
        onClose={() => setDialog(null)}
        onSaved={(stranded) => {
          setDialog(null);
          if (stranded) {
            setToast(`${stranded} ${stranded === 1 ? 'day falls' : 'days fall'} outside the new dates but still hold places, so they were kept.`);
          }
          reload();
        }}
      />
      <ResearchDialog
        open={dialog === 'research'}
        board={board}
        initialFocus={researchFocus}
        onClose={() => setDialog(null)}
        onAdded={(added) => {
          setBoard((b) => (b ? { ...b, stops: [...b.stops, ...added] } : b));
          setToast(`${added.length} ${added.length === 1 ? 'candidate' : 'candidates'} added.`);
        }}
      />
      <LegDialog
        stop={leg}
        onClose={() => setLeg(null)}
        onSave={(s, patch) => {
          setLeg(null);
          commit(patchLocal(s.stop_id, patch), () => patchStop(s.stop_id, patch));
        }}
        onRemove={(s) => {
          setLeg(null);
          remove(s);
        }}
      />
      <EndDialog
        kind={end}
        trip={trip}
        onClose={() => setEnd(null)}
        onSave={(kind, p) => {
          setEnd(null);
          const patch = kind === 'start'
            ? { start_mode: p.mode, start_detail: p.detail, start_time: p.time }
            : { end_mode: p.mode, end_detail: p.detail, end_time: p.time };
          commit((b) => ({ ...b, trip: { ...b.trip, ...patch } }), () => patchTrip(trip.trip_id, patch));
        }}
      />
      <NightsDialog
        days={nights}
        onClose={() => setNights(null)}
        onSave={(ds, patch) => {
          setNights(null);
          // Choosing a town for a night on board means it is no longer spent travelling.
          if (patch.base_city) patch = { ...patch, in_transit: false };
          const ids = new Set(ds.map((d) => d.day_id));
          commit(
            (b) => ({ ...b, days: b.days.map((d) => (ids.has(d.day_id) ? { ...d, ...patch } : d)) }),
            async () => {
              const results = await Promise.all(ds.map((d) => updateDay(d.day_id, patch)));
              return results.find((r) => r.error) ?? { error: null };
            },
          );
        }}
      />
      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} message={toast} />
    </Box>
  );
}

function headline(board: Board, candidates: number): string {
  if (candidates === 0 && !board.stops.some((s) => s.day_id && s.stop_type !== 'transit')) return 'Find what this trip is for';
  if (candidates > 0) return 'Fit the candidates into the days';
  return 'Every candidate has a day';
}

function DarkButton(props: React.ComponentProps<typeof Button> & { component?: React.ElementType; to?: string }) {
  return (
    <Button
      variant="outlined"
      {...props}
      sx={{ color: outputSurface.text, borderColor: outputSurface.muted, '&:hover': { borderColor: outputSurface.text }, ...props.sx }}
    />
  );
}

function EndLine({ kind, trip, onOpen }: { kind: EndKind; trip: Trip; onOpen: (k: EndKind) => void }) {
  const date = kind === 'start' ? trip.start_date : trip.end_date;
  if (!date) return null;
  const place = kind === 'start' ? trip.start_place : trip.end_place;
  const mode = kind === 'start' ? trip.start_mode : trip.end_mode;
  const time = shortTime(kind === 'start' ? trip.start_time : trip.end_time);
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(kind)}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, p: 0.5, ml: -0.5, border: 0, borderRadius: 1, bgcolor: 'transparent', font: 'inherit', color: 'text.secondary', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
    >
      {kind === 'start' ? <OutlinedFlagIcon fontSize="small" /> : <SportsScoreIcon fontSize="small" />}
      <Typography variant="body2" component="span">
        <Box component="strong" sx={{ color: 'text.primary' }}>
          {kind === 'start' ? 'Start' : 'Finish'}
        </Box>{' '}
        {formatDay(date)}
        {place ? ` · ${place}` : ''}
        {mode ? ` · by ${MODE_LABELS[mode].toLowerCase()}` : ''}
        {mode && time ? ` ${time}` : ''}
        {!mode ? (
          <Box component="span" sx={{ color: 'secondary.dark' }}>
            {' '}
            · how not decided
          </Box>
        ) : !time ? (
          <Box component="span" sx={{ color: 'secondary.dark' }}>
            {' '}
            · time not set
          </Box>
        ) : null}
      </Typography>
    </Box>
  );
}

function BasicsDialog({
  open,
  board,
  onClose,
  onSaved,
}: {
  open: boolean;
  board: Board;
  onClose: () => void;
  onSaved: (strandedDays: number) => void;
}) {
  const fromTrip = useCallback((): TripBasics => {
    const t = board.trip;
    return {
      title: t.title,
      destination: t.destination ?? '',
      themes: t.themes,
      brief: t.brief ?? '',
      travelers: t.travelers ?? '',
      start_date: t.start_date,
      start_place: t.start_place ?? '',
      start_mode: t.start_mode,
      start_detail: t.start_detail ?? '',
      start_time: t.start_time,
      end_date: t.end_date,
      end_place: t.end_place ?? '',
      end_mode: t.end_mode,
      end_detail: t.end_detail ?? '',
      end_time: t.end_time,
      dates_precision: t.dates_precision,
    };
  }, [board.trip]);
  const [basics, setBasics] = useState<TripBasics>(fromTrip);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setBasics(fromTrip());
      setError(null);
    }
  }, [open, fromTrip]);

  const problem = basicsProblem(basics);

  async function save() {
    setSaving(true);
    const { error: err, strandedDays } = await updateTripBasics(board.trip.trip_id, cleanBasics(basics));
    setSaving(false);
    if (err) setError(err);
    else onSaved(strandedDays);
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle>Trip basics</DialogTitle>
      <DialogContent dividers>
        <TripBasicsForm value={basics} onChange={setBasics} titleTouched onTitleTouched={() => {}} />
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        {problem ? (
          <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', ml: 1 }}>
            {problem}
          </Typography>
        ) : null}
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving || !!problem}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
